/**
 * Zod schema for text (free text) fields.
 * Messages: Required, Too short, Too long, Invalid characters, Duplicate value.
 */

import { z } from 'zod';
import type { FieldSchemaConfig } from '../types';

export function getTextSchema(col: FieldSchemaConfig): z.ZodTypeAny {
  const required = 'required' in col && col.required === true;
  const minLength = col.minLength ?? undefined;
  const maxChar = col.maxChar != null && col.maxChar > 0 ? col.maxChar : null;
  const lettersNumbersOnly = col.lettersNumbersOnly === true;
  const existingValues = col.existingValues;

  let schema: z.ZodTypeAny = z
    .union([z.string(), z.number(), z.null(), z.undefined()])
    .transform((v: string | number | null | undefined) => (v == null ? '' : String(v)).trim());

  if (required) {
    schema = schema.refine((s: string) => s.length >= 1, 'This field is required.') as z.ZodTypeAny;
  }
  if (minLength != null && minLength > 0) {
    schema = schema.refine(
      (s: string) => s.length === 0 || s.length >= minLength,
      `Enter at least ${minLength} characters.`
    ) as z.ZodTypeAny;
  }
  if (maxChar != null) {
    schema = schema.refine(
      (s: string) => s.length <= maxChar,
      `Must be ${maxChar} characters or fewer.`
    ) as z.ZodTypeAny;
  }
  if (lettersNumbersOnly) {
    schema = schema.refine(
      (s: string) => s.length === 0 || /^[A-Za-z0-9]*$/.test(s),
      'Only letters and numbers are allowed.'
    ) as z.ZodTypeAny;
  }
  if (existingValues != null && existingValues.length > 0) {
    const set = new Set(existingValues.map(v => v.trim().toLowerCase()));
    schema = schema.refine(
      (s: string) => s.length === 0 || !set.has(s.trim().toLowerCase()),
      'This value already exists.'
    ) as z.ZodTypeAny;
  }

  return schema;
}
